import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, unref, type Ref } from 'vue'
import { apiClient } from '@/api/client'
import type { MediaAssetResponse } from '@aannemerplus/types'

export type { MediaAssetResponse }

// Query keys
export const mediaKeys = {
  all: ['media'] as const,
  lists: () => [...mediaKeys.all, 'list'] as const,
  list: (projectId: number) => [...mediaKeys.lists(), { projectId }] as const,
}

// API functions
const fetchProjectMedia = async (projectId: number): Promise<MediaAssetResponse[]> => {
  const response = await apiClient.getProjectMedia(projectId.toString())
  const data = response.data as any
  // Interceptor unwraps { success, data } envelope; result is { items, pagination } or array
  if (data?.items && Array.isArray(data.items)) {
    return data.items
  }
  if (Array.isArray(data)) {
    return data
  }
  return []
}

const uploadMedia = async (vars: {
  projectId: number
  file: File
  altText?: string
}): Promise<MediaAssetResponse> => {
  const formData = new FormData()
  formData.append('file', vars.file)
  if (vars.altText) {
    formData.append('altText', vars.altText)
  }

  const response = await apiClient.uploadMedia(vars.projectId.toString(), formData)
  return response.data as MediaAssetResponse
}

// Query hooks
export const useProjectMediaQuery = (projectId: Ref<number> | number) => {
  const id = computed(() => unref(projectId))

  return useQuery({
    queryKey: computed(() => mediaKeys.list(id.value)),
    queryFn: () => fetchProjectMedia(id.value),
    enabled: computed(() => id.value > 0),
  })
}

// Mutation hooks
export const useUploadMediaMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uploadMedia,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.list(variables.projectId) })
    },
  })
}
